"use client";

import { useState, useCallback, useTransition } from "react";
import { useTranslations } from "next-intl";
import {
  Paper,
  Text,
  TbTrees,
  TbMapPin,
  TbTarget,
  TbEye,
  Button,
  Collapsible,
  Box,
  Flex,
} from "@forestsync/ui";
import { TokenList } from "@/components/glassroom/common";
import { HyperLink } from "@/components/glassroom/common/HyperLink";
import { loadTransactionTokensByForest } from "@/actions/transaction-detail";
import type { TransactionDetail, TokenListItem } from "@/types/glassroom";

interface ForestTokensState {
  tokens: TokenListItem[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
  isLoading: boolean;
}

interface TransactionForestsProps {
  transaction: TransactionDetail;
}

// Forest info component (without name since it's in the Collapsible header)
const ForestInfo = ({ forest }: { forest: any }) => {
  const t = useTranslations();

  return (
    <div className="py-3 px-4 bg-gray-50 dark:bg-gray-800/50 transition-colors duration-150 rounded-md">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <Text
                size="sm"
                className="text-gray-400 dark:text-gray-500 font-mono opacity-75"
              >
                {forest.sinkRefId}
              </Text>
            </span>
            <span className="flex items-center gap-1">
              <TbMapPin
                size={16}
                className="text-green-600 dark:text-green-400"
              />
              {[forest.sinkCountry, forest.sinkRegion, forest.sinkMunicipality]
                .filter(Boolean)
                .join(", ")}
            </span>
            <span className="flex items-center gap-1">
              <TbTarget
                size={16}
                className="text-blue-600 dark:text-blue-400"
              />
              {forest.tokens.length} {t("transaction.affectedTokens")}
            </span>
          </div>
        </div>
        <HyperLink type="sink" refId={forest.sinkRefId}>
          <Button
            variant="light"
            size="xs"
            leftSection={<TbEye size={14} />}
            className="w-full sm:w-auto shrink-0"
          >
            {t("transaction.viewForest")}
          </Button>
        </HyperLink>
      </div>
    </div>
  );
};

export function TransactionForests({ transaction }: TransactionForestsProps) {
  const t = useTranslations();
  const [tokensByForest, setTokensByForest] = useState<
    Record<string, ForestTokensState>
  >({});
  const [openForest, setOpenForest] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  // Load tokens for a specific forest and page
  const loadTokensForForest = useCallback(
    async (forestSinkId: string, page: number = 1): Promise<void> => {
      const currentState = tokensByForest[forestSinkId];

      if (currentState?.isLoading) return;

      setTokensByForest((prev) => ({
        ...prev,
        [forestSinkId]: {
          tokens: currentState?.tokens || [],
          totalCount: currentState?.totalCount || 0,
          totalPages: currentState?.totalPages || 0,
          currentPage: page,
          isLoading: true,
        },
      }));

      return new Promise((resolve, reject) => {
        startTransition(async () => {
          try {
            const result = await loadTransactionTokensByForest(
              transaction.refId,
              forestSinkId,
              page,
              50,
            );

            setTokensByForest((prev) => ({
              ...prev,
              [forestSinkId]: {
                tokens: result.tokens,
                totalCount: result.totalCount,
                totalPages: result.totalPages,
                currentPage: result.page,
                isLoading: false,
              },
            }));
            resolve();
          } catch (error) {
            console.error(
              "Failed to load tokens for forest:",
              forestSinkId,
              error,
            );
            setTokensByForest((prev) => ({
              ...prev,
              [forestSinkId]: {
                tokens: currentState?.tokens || [],
                totalCount: 0,
                totalPages: 0,
                currentPage: page,
                isLoading: false,
              },
            }));
            reject(error);
          }
        });
      });
    },
    [transaction.refId, tokensByForest],
  );

  const handleForestToggle = useCallback(
    (forestSinkId: string, isOpen: boolean) => {
      if (isOpen) {
        setOpenForest(forestSinkId);
        if (!tokensByForest[forestSinkId]) {
          loadTokensForForest(forestSinkId, 1);
        }
      } else {
        setOpenForest(null);
      }
    },
    [tokensByForest, loadTokensForForest],
  );

  const handleTokenPageChange = useCallback(
    async (forestSinkId: string, page: number) => {
      await loadTokensForForest(forestSinkId, page);
    },
    [loadTokensForForest],
  );

  if (transaction.forests.length === 0) {
    return null;
  }

  return (
    <Paper withBorder radius="lg" p="md" className="shadow-md">
      <Text fw={700} size="md" className="mb-4">
        {t("transaction.affectedProjects")} ({transaction.forests.length})
      </Text>
      <div className="space-y-2">
        {transaction.forests.map((forest) => {
          const forestState = tokensByForest[forest.sinkRefId];

          return (
            <Collapsible
              key={forest.sinkRefId}
              label={forest.sinkName}
              count={forest.tokens.length}
              icon={<TbTrees size={18} className="text-green-500" />}
              depth={0}
              color="green"
              expanded={openForest === forest.sinkRefId}
              onToggle={(isOpen) =>
                handleForestToggle(forest.sinkRefId, isOpen)
              }
            >
              <Flex
                pl={{ base: "xs", sm: "md" }}
                gap="xs"
                direction="column"
                pt="xs"
              >
                <ForestInfo forest={forest} />
                <Paper
                  withBorder
                  radius="lg"
                  p="md"
                  className="bg-white dark:bg-gray-900 shadow-md"
                >
                  {forestState ? (
                    forestState.isLoading && forestState.tokens.length === 0 ? (
                      <TokenList
                        tokens={[]}
                        isLoading={true}
                        maxHeight={400}
                        emptyMessage={t("transaction.loadingTokens")}
                        currentPage={1}
                        totalPages={1}
                        onPageChange={async () => {}}
                      />
                    ) : forestState.tokens.length > 0 ? (
                      <TokenList
                        tokens={forestState.tokens}
                        isLoading={false}
                        maxHeight={400}
                        emptyMessage={t("transaction.noTokensFound")}
                        currentPage={forestState.currentPage}
                        totalPages={forestState.totalPages}
                        onPageChange={async (page: number) =>
                          handleTokenPageChange(forest.sinkRefId, page)
                        }
                      />
                    ) : (
                      <Text className="text-gray-500 dark:text-gray-400 text-center py-8">
                        {t("transaction.noTokensFound")}
                      </Text>
                    )
                  ) : (
                    <Text className="text-gray-500 dark:text-gray-400 text-center py-8">
                      {t("transaction.clickToLoadTokens")}
                    </Text>
                  )}
                </Paper>
              </Flex>
            </Collapsible>
          );
        })}
      </div>
    </Paper>
  );
}
